"use client";

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2 } from '@/components/icons';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { voteOnPromoNote, removeVoteFromPromo, getUserVoteOnPromo } from '@/lib/supabase/promos';
import { getVotingPromptText, getVotingContextText, getNoteType } from '@/lib/utils/noteTypeHelpers';
import { useContentSidebar } from '@/context/ContentSidebarContext';

interface PromoVotingPanelProps {
  noteId: string;
  userId?: string;
  upvotes?: number;
  downvotes?: number;
  hotnessScore?: number;
  categoryName?: string | null;
  compact?: boolean;
  initialUserVote?: 'upvote' | 'downvote' | null;
}

const PromoVotingPanel: React.FC<PromoVotingPanelProps> = ({
  noteId,
  userId,
  upvotes = 0,
  downvotes = 0,
  hotnessScore = 0,
  categoryName,
  compact = false,
  initialUserVote,
}) => {
  const { toast } = useToast();
  const { selectedNote, setSelectedNote } = useContentSidebar();
  const [currentVote, setCurrentVote] = useState<'upvote' | 'downvote' | null>(initialUserVote ?? null);
  const [loading, setLoading] = useState(false);
  const [upvoteCount, setUpvoteCount] = useState(upvotes);
  const [downvoteCount, setDownvoteCount] = useState(downvotes);
  const [displayHotness, setDisplayHotness] = useState(hotnessScore);
  const noteType = getNoteType(categoryName);

  useEffect(() => {
    setCurrentVote(initialUserVote ?? null);
  }, [initialUserVote]);

  // Cargar el voto actual del usuario cuando no hay valor inicial cargado
  useEffect(() => {
    if (initialUserVote !== undefined) {
      return;
    }

    const fetchUserVote = async () => {
      if (!userId) {
        setCurrentVote(null);
        return;
      }
      try {
        const vote = await getUserVoteOnPromo(noteId, userId);
        setCurrentVote(vote);
      } catch (error) {
        console.error('Error fetching user vote:', error);
      }
    };
    fetchUserVote();
  }, [noteId, userId, initialUserVote]);

  // Actualizar counts cuando cambian los props (para sincronizar cambios externos)
  useEffect(() => {
    setUpvoteCount(upvotes);
    setDownvoteCount(downvotes);
    setDisplayHotness(hotnessScore);
  }, [upvotes, downvotes, hotnessScore]);

  const updateSelectedNoteVotes = useCallback((newUpvotes: number, newDownvotes: number, newHotness: number, newUserVote?: 'upvote' | 'downvote' | null) => {
    if (selectedNote && selectedNote.id === noteId) {
      setSelectedNote({
        ...selectedNote,
        upvotes: newUpvotes,
        downvotes: newDownvotes,
        hotness_score: newHotness,
        ...(newUserVote !== undefined ? { user_vote: newUserVote } : {}),
      });
    }
  }, [selectedNote, noteId, setSelectedNote]);

  const handleVote = useCallback(async (voteType: 'upvote' | 'downvote') => {
    if (!userId) {
      toast({
        title: 'Debes iniciar sesión',
        description: getVotingPromptText(noteType),
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      let newUpvotes = upvoteCount;
      let newDownvotes = downvoteCount;
      let newHotness = displayHotness;
      let newUserVote: 'upvote' | 'downvote' | null = currentVote === voteType ? null : voteType;

      // Si el usuario ya tiene ese voto, lo removemos (toggle)
      if (currentVote === voteType) {
        await removeVoteFromPromo(noteId, userId);
        setCurrentVote(null);
        
        if (voteType === 'upvote') {
          newUpvotes = upvoteCount - 1;
          newHotness = displayHotness - 1;
          setUpvoteCount(newUpvotes);
          setDisplayHotness(newHotness);
          updateSelectedNoteVotes(newUpvotes, downvoteCount, newHotness, null);
        } else {
          newDownvotes = downvoteCount - 1;
          newHotness = displayHotness + 1;
          setDownvoteCount(newDownvotes);
          setDisplayHotness(newHotness);
          updateSelectedNoteVotes(upvoteCount, newDownvotes, newHotness, null);
        }
        
        toast({
          title: 'Voto removido',
          description: `Tu ${voteType === 'upvote' ? 'voto positivo' : 'voto negativo'} ha sido removido.`,
        });
      } else {
        // Si está votando algo diferente, cambiar el voto
        await voteOnPromoNote(noteId, userId, voteType);
        
        const prevWasUpvote = currentVote === 'upvote';
        const prevWasDownvote = currentVote === 'downvote';
        const newIsUpvote = voteType === 'upvote';

        // Actualizar contadores según lo que cambió
        if (prevWasUpvote && newIsUpvote) {
          // No debería pasar, pero por si acaso
        } else if (prevWasUpvote && !newIsUpvote) {
          newUpvotes = upvoteCount - 1;
          newDownvotes = downvoteCount + 1;
          newHotness = displayHotness - 2;
        } else if (prevWasDownvote && newIsUpvote) {
          newDownvotes = downvoteCount - 1;
          newUpvotes = upvoteCount + 1;
          newHotness = displayHotness + 2;
        } else if (prevWasDownvote && !newIsUpvote) {
          // No debería pasar
        } else {
          // No hay voto anterior
          if (newIsUpvote) {
            newUpvotes = upvoteCount + 1;
            newHotness = displayHotness + 1;
          } else {
            newDownvotes = downvoteCount + 1;
            newHotness = displayHotness - 1;
          }
        }

        setUpvoteCount(newUpvotes);
        setDownvoteCount(newDownvotes);
        setDisplayHotness(newHotness);
        updateSelectedNoteVotes(newUpvotes, newDownvotes, newHotness, voteType);
        setCurrentVote(voteType);
        
        toast({
          title: 'Voto registrado',
          description: `Tu ${voteType === 'upvote' ? 'voto positivo' : 'voto negativo'} ha sido registrado.`,
        });
      }

      // Disparar evento para refrescar el feed de notas
      window.dispatchEvent(new CustomEvent('noteVoteUpdated', {
        detail: {
          noteId,
          upvotes: newUpvotes,
          downvotes: newDownvotes,
          hotnessScore: newHotness,
          userVote: newUserVote,
        }
      }));
    } catch (error) {
      console.error('Error voting on promo:', error);
      toast({
        title: 'Error',
        description: 'No se pudo registrar tu voto. Inténtalo de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [noteId, userId, currentVote, upvoteCount, downvoteCount, displayHotness, toast, updateSelectedNoteVotes]);

  if (compact) {
    return (
      <div className="mt-1 space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Comunidad</span>
          <span className="font-semibold text-foreground">
            {displayHotness >= 0 ? '+' : ''}{displayHotness} · {displayHotness > 0 ? 'Caliente' : displayHotness < 0 ? 'Fria' : 'Neutra'}
          </span>
        </div>

        <div className="flex gap-1.5">
          <Button
            variant={currentVote === 'upvote' ? 'default' : 'outline'}
            className="h-7 px-2.5 flex-1 text-xs"
            onClick={() => handleVote('upvote')}
            disabled={loading}
          >
            <span className="flex items-center gap-1">
              {loading && currentVote === 'upvote' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <ArrowUp className="h-3.5 w-3.5" />
              )}
              <span className="font-semibold">{upvoteCount}</span>
            </span>
          </Button>
          <Button
            variant={currentVote === 'downvote' ? 'default' : 'outline'}
            className="h-7 px-2.5 flex-1 text-xs"
            onClick={() => handleVote('downvote')}
            disabled={loading}
          >
            <span className="flex items-center gap-1">
              {loading && currentVote === 'downvote' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <ArrowDown className="h-3.5 w-3.5" />
              )}
              <span className="font-semibold">{downvoteCount}</span>
            </span>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Card className="mt-4">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Popularidad de la Promo</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Hotness Score Display */}
        <div className="flex items-center justify-between">
          <div className="text-center flex-1">
            <div className="text-2xl font-bold">
              {displayHotness >= 0 ? '+' : ''}{displayHotness}
            </div>
            <p className="text-xs text-muted-foreground">
              {displayHotness > 0 ? '🔥 Caliente' : displayHotness < 0 ? '❄️ Fría' : '⚪ Neutra'}
            </p>
          </div>
        </div>

        {/* Voting Buttons */}
        <div className="flex gap-2">
          <Button
            variant={currentVote === 'upvote' ? 'default' : 'outline'}
            className="flex-1 flex flex-col items-center py-2"
            onClick={() => handleVote('upvote')}
            disabled={loading}
          >
            <span className="flex items-center gap-1">
              {loading && currentVote === 'upvote' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowUp className="h-4 w-4" />
              )}
              <span className="font-bold text-green-600 text-base">{upvoteCount}</span>
            </span>
            <span className="text-xs mt-1">{currentVote === 'upvote' ? 'Votado' : 'Votá positivo'}</span>
          </Button>
          <Button
            variant={currentVote === 'downvote' ? 'default' : 'outline'}
            className="flex-1 flex flex-col items-center py-2"
            onClick={() => handleVote('downvote')}
            disabled={loading}
          >
            <span className="flex items-center gap-1">
              {loading && currentVote === 'downvote' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowDown className="h-4 w-4" />
              )}
              <span className="font-bold text-red-600 text-base">{downvoteCount}</span>
            </span>
            <span className="text-xs mt-1">{currentVote === 'downvote' ? 'Votado' : 'Votá negativo'}</span>
          </Button>
        </div>

        {/* Info */}
        <p className="text-xs text-muted-foreground text-center">
          {getVotingContextText(noteType)}
        </p>
      </CardContent>
    </Card>
  );
};

export default PromoVotingPanel;
